import { Component, createRef } from 'react';
import isEqual from 'lodash-es/isEqual';
import differenceBy from 'lodash-es/differenceBy';
import { connect } from 'react-redux';
import autoBind from 'react-autobind';
import classNames from 'classnames/bind';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo } from '@fortawesome/pro-solid-svg-icons/faRedo';
import { faFilter } from '@fortawesome/pro-solid-svg-icons/faFilter';

import {
  loadFilingsForUser, getRefreshFilings, getNewFilings, getLatestFilings, setLatestFilingData,
  fetchFilingsSources,
} from '../../../../actions/filings.actions';
import { resetAllFiltersAction } from '../../../../actions/filingsFilters.actions';
import withComponentName from '../../../../decorators/withComponentName';
import { commonAPIDataLimit } from '../../../../data/webPageData';

import FilterButton from '../../../Buttons/FilterButton';
import Preloader from '../../../Preloader';
import ProviderFilter from '../../ProviderFilings/ProviderFilter';
import ProviderFilingsLists from '../../ProviderFilings/ProviderFilingsLists';

import StorageSvc from '../../../../services/StorageSvc';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

class ProviderBody extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFilterOpen: false,
      isReset: false,
      isCategoryReset: false,
      isSourceReset: false,
      isInitialFilingsCall: false,
      currentPage: 1,
      hasNextPage: true,
      refreshEvery: 15000,
      newFilingsAvailable: false,
      isFilterFixed: false,
      isNewFilingsFixed: false,
    };
    autoBind(this);
    this.mounted = false;
    this.filingsUpdateTimer = 0;
    this.newFilingsFetching = false;
    this.filings = createRef();
    this.mobileNewFilings = createRef();
    this.cancelToken = axios.CancelToken;
    this.sourceCancelToken = this.cancelToken.source();
  }

  async componentDidMount() {
    this.mounted = true;

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.onResize();

    const {
      filtersInitialized, fetchFilingsSources: propsFetchFilingsSources,
    } = this.props;
    await propsFetchFilingsSources({ active: true });
    if (filtersInitialized) this.loadFilingsData(true);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      filtersInitialized, storyFilters, extraProps, hideFilingsList,
    } = this.props;
    const {
      storyFilters: prevStoryFilters,
      extraProps: prevExtraProps,
      hideFilingsList: prevHideFilingsList,
    } = prevProps;

    const propsParam = {
      filing_period_start_datetime: storyFilters.filing_period_start_datetime,
      filing_period_end_datetime: storyFilters.filing_period_end_datetime,
      filing_source_ids: storyFilters.filing_source_ids,
      filing_category_ids: storyFilters.filing_category_ids,
      filing_type_ids: storyFilters.filing_type_ids,
      filing_categories_all: storyFilters.filing_categories_all,
      filing_types_all: storyFilters.filing_types_all,
      filings_time_filter: storyFilters.filings_time_filter,
    };
    const prevPropsParam = {
      filing_period_start_datetime: prevStoryFilters.filing_period_start_datetime,
      filing_period_end_datetime: prevStoryFilters.filing_period_end_datetime,
      filing_source_ids: prevStoryFilters.filing_source_ids,
      filing_category_ids: prevStoryFilters.filing_category_ids,
      filing_type_ids: prevStoryFilters.filing_type_ids,
      filing_categories_all: prevStoryFilters.filing_categories_all,
      filing_types_all: prevStoryFilters.filing_types_all,
      filings_time_filter: prevStoryFilters.filings_time_filter,
    };
    if (
      (filtersInitialized && prevProps.filtersInitialized !== filtersInitialized)
      || !isEqual(extraProps, prevExtraProps)
    ) {
      this.loadFilingsData(true);
    } else if (filtersInitialized && !isEqual(propsParam, prevPropsParam)) {
      this.loadFilingsData();
    }

    const { isReset, isCategoryReset, isSourceReset } = this.state;
    if (isReset && prevState.isReset !== isReset) {
      this.setState({
        isReset: false,
      });
    }
    if (isCategoryReset && prevState.isCategoryReset !== isCategoryReset) {
      this.setState({
        isCategoryReset: false,
      });
    }
    if (isSourceReset && prevState.isSourceReset !== isSourceReset) {
      this.setState({
        isSourceReset: false,
      });
    }

    if (!hideFilingsList && hideFilingsList !== prevHideFilingsList) {
      const { refreshEvery } = this.state;
      this.filingsUpdateTimer = setTimeout(this.checkForUpdate, refreshEvery);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    clearTimeout(this.filingsUpdateTimer);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.onResize);
    this.sourceCancelToken.cancel('');
  }

  onResize() {
    this.setState({
      width: window.innerWidth,
    });
  }

  async checkForUpdate() {
    if (!this.mounted) return;
    const {
      getRefreshFilings: propsGetRefreshFilings,
      filings, extraProps, hasSourceAccess, hideFilingsList,
    } = this.props;

    if (hideFilingsList) {
      return;
    }

    const param = {
      per: 20,
      ...extraProps,
    };
    const extra = {
      cancelToken: this.sourceCancelToken.token,
      hasSourceAccess,
    };
    const data = await propsGetRefreshFilings(param, extra);

    const newData = differenceBy(data, filings, 'filingId');

    if (!this.mounted) return;
    const { refreshEvery, isFilterFixed } = this.state;
    if (newData.length) {
      if (isFilterFixed) {
        this.setState({
          newFilingsAvailable: true,
        });
      } else {
        const { setLatestFilingData: propsSetLatestFilingData } = this.props;
        propsSetLatestFilingData(newData);
        this.filingsUpdateTimer = setTimeout(this.checkForUpdate, refreshEvery);
      }
    } else {
      this.filingsUpdateTimer = setTimeout(this.checkForUpdate, refreshEvery);
    }
  }

  scrollToStart() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    this.setState({
      newFilingsAvailable: false,
    });
    const {
      getLatestFilings: propsGetLatestFilings,
      extraProps, hasSourceAccess,
    } = this.props;

    const extra = {
      hasSourceAccess,
    };
    propsGetLatestFilings({
      ...extraProps,
      per: 20,
    }, extra);

    const { refreshEvery } = this.state;
    this.filingsUpdateTimer = setTimeout(this.checkForUpdate, refreshEvery);
  }

  handleScroll() {
    const docTop = this.filings.current.offsetTop;
    const docHeight = this.filings.current.offsetHeight;
    const winheight = window.innerHeight === undefined
      ? document.documentElement.clientHeight
      : window.innerHeight;
    const scrollpoint = window.scrollY === undefined
      ? window.document.documentElement.scrollTop
      : window.scrollY;

    const filingTop = this.filings.current.getBoundingClientRect().top;
    const { hasTabHeader } = this.props;
    const isFilterFixed = hasTabHeader ? (filingTop - 40) <= 0 : filingTop <= 0;

    const newFilingTop = this.mobileNewFilings.current.getBoundingClientRect().top;
    const isNewFilingsFixed = hasTabHeader ? (newFilingTop - 40) <= 0 : newFilingTop <= 0;

    this.setState({
      isFilterFixed,
      isNewFilingsFixed,
    });

    if ((scrollpoint + winheight) >= (docHeight + docTop)) {
      this.loadNewFilingsData();
    }
  }

  async loadNewFilingsData() {
    const { hasNextPage, currentPage } = this.state;
    const {
      getNewFilings: propsGetNewFilings,
      loading, filingsPageLoad, extraProps, hasSourceAccess,
    } = this.props;

    if (this.newFilingsFetching || !hasNextPage || loading || filingsPageLoad) return;

    const param = {
      per: commonAPIDataLimit,
      page: currentPage + 1,
      ...extraProps,
    };
    const extra = {
      cancelToken: this.sourceCancelToken.token,
      hasSourceAccess,
    };

    this.newFilingsFetching = true;
    const data = await propsGetNewFilings(param, extra);
    this.newFilingsFetching = false;

    if (!this.mounted) return;
    if (data.length < commonAPIDataLimit) {
      this.setState({
        hasNextPage: false,
      });
    } else {
      this.setState({
        hasNextPage: true,
        currentPage: currentPage + 1,
      });
    }
  }

  loadFilingsData(isInitialCall) {
    const {
      loadFilingsForUser: propsLoadFilingsForUser,
      extraProps, hasSourceAccess, hideFilingsList,
    } = this.props;

    if (hideFilingsList) {
      return;
    }

    const param = { per: commonAPIDataLimit, ...extraProps };
    const extra = {
      cancelToken: this.sourceCancelToken.token,
      hasSourceAccess,
      isInitialCall,
    };

    propsLoadFilingsForUser(param, extra)
      .then((data) => {
        this.setState({
          hasNextPage: data.length >= commonAPIDataLimit,
        });
        if (isInitialCall) {
          const { refreshEvery } = this.state;
          this.filingsUpdateTimer = setTimeout(this.checkForUpdate, refreshEvery);
        }
      })
      .catch(() => {
        if (isInitialCall) {
          const { refreshEvery } = this.state;
          this.filingsUpdateTimer = setTimeout(this.checkForUpdate, refreshEvery);
        }
      });

    this.setState({
      isInitialFilingsCall: true,
      currentPage: 1,
    });
  }

  handleFilterButtonClick() {
    const { isFilterOpen } = this.state;
    this.setState({
      isFilterOpen: !isFilterOpen,
    });
  }

  handleFilterButtonClose() {
    this.setState({
      isFilterOpen: false,
    });
  }

  handleFilterResetClick() {
    const { token, resetAllFiltersAction: propsResetAllFiltersAction, piwikEnabled } = this.props;

    StorageSvc.setItem('FilingsTimeFilter', JSON.stringify({
      start_date: '', end_date: '', filings_time_filter: 'lifetime',
    }));
    propsResetAllFiltersAction(token);
    this.setState({
      isReset: true,
    });

    if (piwikEnabled) {
      _paq.push(['trackEvent', 'Filings', 'Reset btn click']);
    }
  }

  handleFilterRefreshClick() {
    this.loadFilingsData();
  }

  handleFilterReset(key, value) {
    this.setState({
      [key]: value,
    });
  }

  calculateTop() {
    const { hasTabHeader, statePromo } = this.props;
    const { height } = statePromo;
    return height + (hasTabHeader ? 40 : 0);
  }

  renderFilingsList() {
    const { isInitialFilingsCall, hasNextPage } = this.state;
    const {
      loading,
      filings,
      filingsPageLoad,
      isExpandAll,
      newFilingsLoading,
      hasSourceAccess,
      hideFilingsList,
      isSource,
      isCompany,
    } = this.props;

    if (hideFilingsList) {
      return (
        <div className={Styles.filings_no_content}>
          No content was found that meets your criteria
        </div>
      );
    }
    if (loading || (filingsPageLoad && !filings.length)) {
      return (
        <div className={Styles.filings_loader_holder}>
          <Preloader loading />
        </div>
      );
    }
    return (
      <ProviderFilingsLists
        isSource={isSource}
        isCompany={isCompany}
        filings={filings}
        filingsPageLoad={filingsPageLoad}
        isExpandAll={isExpandAll}
        handleFilterReset={this.handleFilterReset}
        onReset={this.handleFilterResetClick}
        isInitialFilingsCall={isInitialFilingsCall}
        newFilingsLoading={newFilingsLoading}
        hasSourceAccess={hasSourceAccess}
        hasNextPage={hasNextPage}
      />
    );
  }

  render() {
    const {
      isFilterOpen, isReset, isSourceReset, isCategoryReset,
      isFilterFixed, newFilingsAvailable, width, isNewFilingsFixed,
    } = this.state;
    const { hasSourceAccess, hasTabHeader, isWatchlists, piwikEnabled } = this.props;

    const newFilingsClass = cx('new_filings_button',
      { show: newFilingsAvailable, hasTab: hasTabHeader, fixed: isFilterFixed && width > 767 });
    const newFilingsMobikeClass = cx('new_filings_button',
      { show: newFilingsAvailable, hasTab: hasTabHeader, fixed: isNewFilingsFixed });

    const filterTop = this.calculateTop();

    return (
      <div className={cx('filings_body', { watchlists: isWatchlists })} ref={this.filings}>
        <div className={Styles.filings_filter_buttons_holder}>
          <div className={Styles.filings_filter_buttons}>
            <FilterButton
              icon={<FontAwesomeIcon icon={faRedo} />}
              content="Refresh"
              onClick={this.handleFilterRefreshClick}
            />
            <FilterButton
              icon={<FontAwesomeIcon icon={faFilter} />}
              content="Filters"
              active={isFilterOpen}
              onClick={this.handleFilterButtonClick}
            />
            <div
              className={newFilingsClass}
              onClick={this.scrollToStart}
              style={{ top: `${isFilterFixed && width > 767 ? filterTop + 15 : 0}px` }}
            >
              <FontAwesomeIcon icon={faRedo} />
              <span className={Styles.new_filings_button_label}>See New Filings</span>
            </div>
          </div>
          <ProviderFilter
            isFilterOpen={isFilterOpen}
            isReset={isReset}
            onReset={this.handleFilterResetClick}
            isSourceReset={isSourceReset}
            isCategoryReset={isCategoryReset}
            piwikEnabled={piwikEnabled}
            hasSourceAccess={hasSourceAccess}
            handleFilterClose={this.handleFilterButtonClose}
          />
          <div
            ref={this.mobileNewFilings}
            className={cx('new_filings_button_mobile', { show: newFilingsAvailable })}
          >
            <div
              className={newFilingsMobikeClass}
              onClick={this.scrollToStart}
              style={{ top: `${filterTop + 15}px` }}
            >
              <FontAwesomeIcon icon={faRedo} />
              <span className={Styles.new_filings_button_label}>See New Filings</span>
            </div>
          </div>
        </div>
        {this.renderFilingsList()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  token: state.watchlistReducer.userToken,
  storyFilters: state.storyFilters,
  filtersInitialized: state.storyFilters.filtersInitialized,
  loading: state.filings.loading,
  filings: state.filings.filings,
  filingsPageLoad: state.filings.filingsPageLoad,
  newFilingsLoading: state.filings.newFilingsLoading,
  isExpandAll: state.storyFilters.expand_filings,
  statePromo: state.promobar,
});

const mapDispatchToProps = {
  loadFilingsForUser,
  resetAllFiltersAction,
  getRefreshFilings,
  getNewFilings,
  getLatestFilings,
  setLatestFilingData,
  fetchFilingsSources,
};

export default withComponentName(
  connect(mapStateToProps, mapDispatchToProps)(ProviderBody),
);
